<template>
  <v-dialog
    v-model="open"
    persistent
    max-width="392"
    data-testid="routineDialog"
  >
    <v-card>
      <v-form v-model="isFormValid">
        <v-card-title class="font-weight-bold pb-6">
          {{
            $t(
              isEditing
                ? 'routines.edit_routine_title'
                : 'routines.start_routine_title'
            )
          }}
        </v-card-title>

        <v-card-text class="pb-0">
          <v-textarea
            data-testid="routineDialog-input-name"
            dense
            outlined
            autofocus
            auto-grow
            rows="1"
            required
            v-model="executionName"
            :class="[isFormValid && 'mb-4']"
            :hide-details="isFormValid"
            :label="`${this.$t('common.name')}*`"
            :rules="[
              name =>
                !!name.trim() || $t('validation.form.this_field_is_required'),
            ]"
          />

          <LocationSelector
            verticalLayout
            siteTourId="routine-start-site-selector"
            :availableTagIds="presetTagIds"
            :organizationId="organizationId"
            :location="initialLocation"
            @change="locationSelectorChange"
          />

          <div class="d-flex">
            <TeamSelector
              class="me-3"
              :disabled="isLoading"
              :routineSiteId="siteId"
              :selectedTeamId="form.selectedTeam"
              @change="handleTeamChange"
            />
            <v-tooltip top max-width="270px">
              <template v-slot:activator="{ on }">
                <v-icon v-on="on">
                  mdi-help-circle-outline
                </v-icon>
              </template>
              {{ $t('routines.team_helper') }}
            </v-tooltip>
          </div>
        </v-card-text>
        <v-card-actions class="justify-end pt-6 pb-4 px-6">
          <Button
            plain
            color="grey"
            data-testid="routineDialog-button-cancel"
            :disabled="isLoading"
            :text="$t('common.button.cancel')"
            @click="handleCancel"
          />

          <Button
            color="primary"
            data-testid="routineDialog-button-start"
            :loading="isLoading"
            :disabled="!isFormValid"
            :text="
              $t(
                isEditing
                  ? 'common.button.save'
                  : 'routines.start_routine_button'
              )
            "
            @click="() => (isEditing ? editRoutine() : startRoutine())"
          />
        </v-card-actions>
      </v-form>
    </v-card>
  </v-dialog>
</template>

<script>
import { mapGetters } from 'vuex'
import { utils } from 'frontend-js-shared'

import api from '@/api'

import mixin from '@/mixins/mixin.vue'
import trackerMixin from '@/mixins/tracking/trackerMixin.vue'

import Button from '@/components/UI/Button.vue'
import TeamSelector from '@/components/RoutineTeamSelector.vue'
import LocationSelector from './LocationSelector/LocationSelector.vue'
import getEntityId from '@/lib/getEntityId'

export default {
  name: 'RoutineStartModal',

  components: {
    TeamSelector,

    LocationSelector,
    Button,
  },

  mixins: [mixin, trackerMixin],

  props: {
    open: Boolean,
    routine: {
      type: Object,
      default: () => ({}),
    },
    isEditing: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      form: {
        selectedTeam: null,
        tags: [],
      },
      isFormValid: true,
      isLoading: false,
      executionName: this.setDefaultName(),
      initialLocation: {
        organization: '',
        site: '',
        tags: [],
      },
      routineDefinition: null,
    }
  },

  mounted() {
    if (this.isEditing) {
      this.fetchRoutineDefinition()
    }

    this.initialLocation = {
      organization: this.organizationId,
      tags: this.isEditing ? this.routine?.tags?.map?.(getEntityId) : [],
      site: this.isEditing
        ? getEntityId(this.routine?.site)
        : getEntityId(this.account?.site),
    }

    this.form = {
      ...this.form,
      site: this.isEditing
        ? getEntityId(this.routine?.site)
        : getEntityId(this.account?.site),
      tags: [...this.initialLocation.tags],
      selectedTeam: this.routine?.selectedTeam?.id ?? null,
    }
  },

  watch: {
    routine() {
      this.executionName = this.setDefaultName()
    },
  },

  computed: {
    ...mapGetters(['organizationId', 'account']),

    tagsValue() {
      return this.form.tags
    },

    siteId() {
      return getEntityId(this.routine?.site)
    },

    presetTagIds() {
      const tags = this.isEditing
        ? this.routineDefinition?.presetTags ?? []
        : this.routine?.presetTags ?? []

      return tags.map(getEntityId)
    },
  },

  methods: {
    setDefaultName() {
      if (!this.routine) return ''
      if (this.isEditing) return this.routine.name

      const date = utils.dates.formatDate(new Date(), 'MMM D, YYYY, h:mm A')
      return `${this.routine.name} - ${date}`
    },

    handleTeamChange(teamId) {
      this.form = {
        ...this.form,
        selectedTeam: teamId,
      }
    },

    locationSelectorChange(data) {
      this.initialLocation = {
        ...this.initialLocation,
        ...data,
      }

      this.form = {
        ...this.form,
        site: data.site,
        tags: data.tags,
      }
    },

    handleCancel() {
      this.form = {}
      this.executionName = this.setDefaultName()
      this.$emit('cancel')
    },

    async startRoutine() {
      try {
        this.isLoading = true

        const response = await api.routine.start(this.organizationId, {
          name: this.executionName.trim(),
          routineDefinition: this.routine?.id,
          ...this.form,
        })

        this.trackStartRoutine({
          name: this.executionName.trim(),
          routineDefinition: this.routine?.id,
          hasTeam: Boolean(this.form.team),
          hasTags: Boolean(this.form?.tags?.length),
          isFromQr: false,
        })

        this.$emit('started', response?.data?.id)
      } catch (err) {
        this.handleRequestError(err)
      } finally {
        this.isLoading = false
      }
    },

    async editRoutine() {
      try {
        this.isLoading = true

        await api.routine.updateRoutineExecution(
          this.organizationId,
          this.routine?.id,
          { ...this.form, name: this.executionName.trim() }
        )

        this.$emit('saved')
      } catch (err) {
        this.handleRequestError(err)
      } finally {
        this.isLoading = false
      }
    },

    async fetchRoutineDefinition() {
      try {
        this.isLoading = true

        const response = await api.routine.getById(
          this.organizationId,
          this.routine?.routineDefinition?._id ??
            this.routine?.routineDefinition
        )

        this.routineDefinition = response?.data
      } catch (err) {
        this.handleRequestError(err)
      } finally {
        this.isLoading = false
      }
    },
  },
}
</script>
